import React from "react";
import { AlertCircle, CheckCircle2, Loader2, X, XCircle } from "lucide-react";
import { useCgaIngestionStore, ActiveCgaIngestionJob } from "../../../stores/cgaIngestionStore";
import { useDiscoveryStore } from "../../../stores/discoveryStore";

function formatStage(stage?: string): string {
  if (!stage) return "Processing";
  return stage
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function IngestionJobCard({ job }: { job: ActiveCgaIngestionJob }) {
  const dismiss = useCgaIngestionStore((s) => s.dismiss);

  const isRunning = job.status === "PENDING" || job.status === "PROCESSING";
  const isFailed = job.status === "FAILED";
  const isDone = job.status === "COMPLETED";
  const isDoneWithErrors = job.status === "COMPLETED_WITH_ERRORS";

  React.useEffect(() => {
    if (!isDone && !isDoneWithErrors) return;
    const timer = setTimeout(() => {
      dismiss(job.jobId);
    }, 3500);
    return () => clearTimeout(timer);
  }, [isDone, isDoneWithErrors, dismiss, job.jobId]);

  const barColor = isFailed
    ? "bg-red-500"
    : isDoneWithErrors
    ? "bg-amber-500"
    : isDone
    ? "bg-emerald-500"
    : "bg-indigo-500";

  return (
    <div className="bg-card border border-border rounded-xl shadow-2xl p-3 w-72 space-y-2.5">
      {/* Header row */}
      <div className="flex items-center gap-2">
        {isRunning ? (
          <Loader2 className="size-4 text-indigo-400 animate-spin shrink-0" />
        ) : isFailed ? (
          <XCircle className="size-4 text-red-400 shrink-0" />
        ) : isDoneWithErrors ? (
          <AlertCircle className="size-4 text-amber-400 shrink-0" />
        ) : (
          <CheckCircle2 className="size-4 text-emerald-400 shrink-0" />
        )}
        <span
          className="flex-1 text-sm font-semibold text-foreground truncate"
          title={job.fileName}
        >
          {job.fileName}
        </span>
        {(isFailed || isDone || isDoneWithErrors) && (
          <button
            onClick={() => dismiss(job.jobId)}
            className="p-0.5 hover:bg-muted rounded transition-colors"
            title="Dismiss"
          >
            <X className="size-3.5 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full ${barColor} transition-all duration-[400ms] ease-out`}
          style={{ width: `${Math.max(job.displayedPct, isRunning ? 5 : 0)}%` }}
        />
      </div>

      {/* Status label */}
      <p className="text-xs">
        {isFailed ? (
          <span className="text-red-400 truncate block">
            {job.errorSummary || "Import failed"}
          </span>
        ) : isDoneWithErrors ? (
          <span className="text-amber-400 truncate block">
            Completed with errors
          </span>
        ) : isDone ? (
          <span className="text-emerald-400">
            Complete — {job.completedCrCount > 0 ? `${job.completedCrCount} CRs imported` : "Import finished"}
          </span>
        ) : (
          <span className="text-muted-foreground">
            {formatStage(job.currentStage)}…{" "}
            <span className="font-semibold text-foreground">{job.displayedPct}%</span>
            {job.totalFileCount > 0 ? (
              <span className="ml-1">
                ({job.processedFileCount}/{job.totalFileCount} files)
              </span>
            ) : job.totalCrCount > 0 ? (
              <span className="ml-1">
                ({job.completedCrCount}/{job.totalCrCount} CRs)
              </span>
            ) : null}
          </span>
        )}
      </p>
    </div>
  );
}

export function CgaIngestionProgressBanner() {
  const jobs = useCgaIngestionStore((s) => s.jobs);
  // Primitive boolean selector prevents unstable array reference from triggering infinite re-renders
  const hasDiscoveryRuns = useDiscoveryStore((s) => Object.keys(s.runs).length > 0);

  const entries = Object.values(jobs);
  if (entries.length === 0) return null;

  const bottomClass = hasDiscoveryRuns ? "bottom-36" : "bottom-6";

  return (
    <div className={`fixed ${bottomClass} right-6 z-50 flex flex-col gap-2 transition-all`}>
      {entries.map((job) => (
        <IngestionJobCard key={job.jobId} job={job} />
      ))}
    </div>
  );
}
